<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="info-content-container flex-column">
        <span class="sac__font-18 bold"> Tune-up Service Mapping </span>
      </div>
      <button type="button" class="ac-close-button" @click="toggle(true, 'titan-edit-integration')">
        <img src="@shared/assets/img/general/x-black.png" alt="x" class="ac-close" />
      </button>
    </div>
    <div class="ac-body-container">
      <div class="grid-row gap-16">
        <p class="sac__font-14">
          Select and map the ServiceTitan Job Types that represent Tune-up to ensure proper tracking
          in SmartAC.com. This allows you to track all the Tune-up related services in SmartAC.com.
        </p>
        <div class="grid-column ai-c">
          <div class="grid-column ai-c jc-s gap-16">
            <div class="sac__font-16 semi-bold">ServiceTitan Job Types</div>
            <img
              src="@shared/assets/img/general/mapping-icon.png"
              alt="arrows"
              class="img-size-24"
            />
            <div class="sac__font-16 semi-bold grid-item js-c">Tune-up</div>
          </div>
        </div>
        <div v-for="(m, idx) in tuneupMappings" :key="idx" class="grid-column ai-c jc-s gap-16">
          <div style="width: 450px">
            {{ m.internalId }}
          </div>
          <button type="button" class="grid-item js-s" @click="removeJobType(m.externalId)">
            <img
              src="@shared/assets/img/general/trash-red.svg"
              class="img-size-20 clickable"
              alt="remove"
            />
          </button>
        </div>
        <div v-if="showInput" style="width: 450px">
          <SACAutoComplete
            placeholder="Search Job Type.."
            label="name"
            :searchMethod="(search) => searchJobTypes(search)"
            queryKey="search"
            :select="(s) => selectJobType(s)"
          />
        </div>
        <button type="button" class="sac__button bg-primary grid-item js-s" @click="addJobType">
          + Add another job type
        </button>
      </div>
    </div>
    <div class="ac-footer-container">
      <button
        type="button"
        class="info-form-button-outline mr-2"
        @click="toggle(true, 'titan-edit-integration')"
      >
        Cancel
      </button>
      <button type="button" class="info-form-button" @click="submit">Save</button>
    </div>
    <ProcessOverlay :show="loadingTuneups" text="Saving..." />
  </div>
</template>

<script>
import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import { mapState } from 'vuex';
import SACAutoComplete from '@shared/components/sac/SACAutoComplete.vue';
import MapType from '@shared/lib/constant/MapType';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

export default {
  name: 'MapTuneupJobTypesPopup',
  components: {
    ProcessOverlay,
    SACAutoComplete,
  },
  mixins: [customerDetailMixin],
  props: {
    toggle: Function,
  },
  data() {
    return {
      jobTypesList: [],
      tuneupMappings: [],
      loadingTuneups: false,
      isAddingJobType: false,
      errorMessage: null,
    };
  },
  computed: {
    ...mapState({
      jobTypeListObject: (state) => state.serviceTitan.jobTypes,
      existingTuneupMappings: (state) => state.serviceTitan.tuneupMappings,
    }),
    showInput() {
      return this.isAddingJobType || !this.tuneupMappings.length;
    },
  },
  watch: {
    existingTuneupMappings: {
      handler(newVal, oldVal) {
        this.tuneupMappings =
          Array.isArray(newVal) && newVal.length
            ? newVal.map((v) => ({ externalId: Number(v.externalId), internalId: v.internalId }))
            : [];
      },
      deep: true,
      immediate: true,
    },
  },
  async mounted() {
    this.jobTypesList = Object.values(this.jobTypeListObject.jobTypes);
  },
  methods: {
    async searchJobTypes(search) {
      const regex = new RegExp(search, 'i');
      return this.jobTypesList.filter((jt) => jt.name && regex.test(jt.name));
    },
    addJobType() {
      this.isAddingJobType = true;
    },
    removeJobType(externalId) {
      this.tuneupMappings = this.tuneupMappings.filter((item) => item.externalId !== externalId);
    },
    selectJobType(item) {
      const isAdded = this.tuneupMappings.find((s) => s.externalId === item.id);
      if (!isAdded) {
        this.tuneupMappings = [
          ...this.tuneupMappings,
          { internalId: item.name, externalId: item.id },
        ];
      }
      this.isAddingJobType = false;
    },
    async submit() {
      this.loadingTuneups = true;
      const addPayload = this.tuneupMappings.map((item) => ({
        externalId: item.externalId,
        internalId: item.internalId,
        mapType: MapType.TUNEUP_JOB_TYPE,
        externalSource: 'Service Titan',
      }));

      const deletePayload = this.existingTuneupMappings
        .filter(
          (item) =>
            !this.tuneupMappings.some((s) => Number(s.externalId) === Number(item.externalId)),
        )
        .map((item) => ({
          ...item,
          externalId: -1,
        }));
      const payload = [...addPayload, ...deletePayload];
      await this.$store.dispatch('saveMapping', payload);
      this.loadingTuneups = false;
      this.toggle(true, 'titan-edit-integration');
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-card-content {
  .ac-header-container {
    display: flex;
    justify-content: space-between;
    .info-content-container {
      align-items: flex-start;
      display: inline-flex;
      gap: 8px;
      width: 100%;
    }
  }
  .info-form-button {
    padding: 12px;
    height: 48px;
    border-radius: 6px;
    background-color: $NBLUE100;
    font-weight: 600;
    color: $WHITE;
    width: 155px;
  }
  .info-form-button-outline {
    padding: 12px;
    height: 48px;
    border-radius: 4px;
    border: 2px solid $NBLUE100;
    color: $NBLUE100;
    font-weight: 600;
    width: 155px;
  }
}
</style>
